import { useCallback, useRef, useState } from "react"

import type { CommandExecutionOptions } from "../remote/remote-client"
import type { DataDependencyInput } from "../data/data-invalidation"
import {
  RemoteCommandFailure,
  type RemoteCommand,
  type RemoteCommandEventDefinitions,
  type RemoteCommandOutcome,
  type RemoteCommandResult,
} from "../remote/remote-command"
import { executeApplicationAction, useActionRuntime } from "./application-action-runtime"
import { useApplicationScope } from "./application-provider"

type MaybePromise<T> = T | Promise<T>

export type ApplicationActionConcurrency = "reject" | "serialize"

export type ApplicationActionStatus = "idle" | "pending" | "succeeded" | "failed" | "error" | "aborted"

export interface ApplicationActionOptions {
  /** Required so every action makes its concurrency policy explicit. */
  readonly concurrency: ApplicationActionConcurrency
  /** Optional owner lifetime, for example a use-case signal. */
  readonly signal?: AbortSignal
  /** Optional presentation/feature handling for a business failure: the RemoteCommandFailure of the failed command. */
  readonly onFailure?: (failure: RemoteCommandFailure) => MaybePromise<void>
  /** Optional presentation/feature handling for a technical error. Errors are reported to telemetry automatically. */
  readonly onError?: (error: unknown) => MaybePromise<void>
}

export type ApplicationActionRunResult<TResult> =
  | { readonly status: "succeeded"; readonly value: TResult }
  | { readonly status: "failed"; readonly failure: RemoteCommandFailure }
  | { readonly status: "error"; readonly error: unknown }
  /**
   * The client stopped waiting because the action owner was aborted.
   * This does not imply that a remote command was not executed by the server.
   */
  | { readonly status: "aborted" }
  | { readonly status: "rejected"; readonly reason: "busy" }

export interface ApplicationActionContext {
  readonly signal: AbortSignal
  /** Explicit fallback for operations that do not publish invalidating domain events. */
  invalidate(...dependencies: readonly DataDependencyInput[]): void
  /**
   * Executes a business command inside this action with `remote.commands.run`.
   * A result with isSucceeded=false throws a RemoteCommandFailure, which ends the action as "failed"
   * (use `failure.eventsOf(command)` to tell which command failed and read its violations).
   * A succeeded command returns its outcome: the `value` and one typed property per declared event
   * (the event, or undefined when the server published none; `all.<Name>` holds every event of that type).
   * Declared events also reach the messenger before the outcome is returned.
   */
  command<TInput, TValue, TEvents extends RemoteCommandEventDefinitions | undefined>(
    command: RemoteCommand<TInput, RemoteCommandResult<TValue>, TEvents>,
    options?: Omit<CommandExecutionOptions, "signal">
  ): Promise<RemoteCommandOutcome<TValue, TEvents>>
  /**
   * Executes any command inside this action with `remote.commands.execute` and returns its raw result:
   * a plain value, `void`, a `RemoteFile`, or a `RemoteCommandResult` that the caller inspects itself.
   * Use `command` for business commands, so that isSucceeded=false ends the action as "failed".
   * Declared events reach the messenger before the result is returned.
   */
  execute<TInput, TResult, TEvents extends RemoteCommandEventDefinitions | undefined>(
    command: RemoteCommand<TInput, TResult, TEvents>,
    options?: Omit<CommandExecutionOptions, "signal">
  ): Promise<TResult>
}

export interface ApplicationAction {
  readonly status: ApplicationActionStatus
  readonly isPending: boolean
  readonly failure: RemoteCommandFailure | undefined
  readonly error: unknown
  /**
   * Runs one complete user action. Business failures, technical errors and owner
   * aborts are returned as outcomes instead of being thrown to the caller.
   */
  run<TResult>(
    execute: (context: ApplicationActionContext) => MaybePromise<TResult>
  ): Promise<ApplicationActionRunResult<TResult>>
}

interface ActionState {
  readonly status: ApplicationActionStatus
  readonly failure: RemoteCommandFailure | undefined
  readonly error: unknown
}

const idleState: ActionState = { status: "idle", failure: undefined, error: undefined }
const pendingState: ActionState = { status: "pending", failure: undefined, error: undefined }

export function useApplicationAction(options: ApplicationActionOptions): ApplicationAction {
  const scope = useApplicationScope()
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const optionsRef = useRef(options)
  optionsRef.current = options
  const [state, setState] = useState<ActionState>(idleState)
  const getRuntime = useActionRuntime(options.signal, (runtime) => {
    if (runtime.pendingCount > 0) setState({ status: "aborted", failure: undefined, error: undefined })
  })

  const run = useCallback(
    async <TResult>(
      execute: (context: ApplicationActionContext) => MaybePromise<TResult>
    ): Promise<ApplicationActionRunResult<TResult>> => {
      const runtime = getRuntime()
      if (runtime.disposed || runtime.controller.signal.aborted) return { status: "aborted" }

      if (optionsRef.current.concurrency === "reject" && runtime.pendingCount > 0) {
        return { status: "rejected", reason: "busy" }
      }

      runtime.pendingCount += 1
      setState(pendingState)

      const executeOne = async (): Promise<ApplicationActionRunResult<TResult>> => {
        const result = await executeApplicationAction(scopeRef.current, runtime.controller.signal, execute, {
          onFailure: (failure) => optionsRef.current.onFailure?.(failure),
          onError: (error) => optionsRef.current.onError?.(error),
        })
        runtime.pendingCount -= 1
        if (!runtime.disposed && runtime.pendingCount === 0) {
          setState(stateFromResult(result))
        }
        return result
      }

      if (optionsRef.current.concurrency === "serialize") {
        const scheduled = runtime.tail.then(executeOne)
        runtime.tail = scheduled.then(() => undefined)
        return scheduled
      }
      return executeOne()
    },
    [getRuntime]
  )

  return {
    status: state.status,
    isPending: state.status === "pending",
    failure: state.failure,
    error: state.error,
    run,
  }
}

function stateFromResult<TResult>(result: ApplicationActionRunResult<TResult>): ActionState {
  switch (result.status) {
    case "succeeded":
      return { status: "succeeded", failure: undefined, error: undefined }
    case "failed":
      return { status: "failed", failure: result.failure, error: undefined }
    case "error":
      return { status: "error", failure: undefined, error: result.error }
    case "aborted":
      return { status: "aborted", failure: undefined, error: undefined }
    case "rejected":
      return idleState
  }
}
